import type { Language } from '../i18n/translations'

export interface BlogContentSection {
  id: string
  heading: string
  paragraphs: string[]
  list?: string[]
  quote?: string
  table?: {
    headers: string[]
    rows: string[][]
  }
}

export interface BlogSeo {
  title: string
  description: string
  keywords: string
}

export interface BlogArticle {
  id: string
  slug: string
  title: string
  excerpt: string
  category: string
  date: string
  updatedAt?: string
  readTime: string
  author: string
  authorInitials: string
  featured?: boolean
  coverColor: string
  coverImage?: string
  content: {
    lead: string
    sections: BlogContentSection[]
  }
  seo?: Partial<Record<Language, BlogSeo>>
  relatedService?: string
  relatedServiceLabel?: string
  relatedServiceCta?: string
  tags: string[]
}

export const BLOG_CATEGORIES = [
  'Все',
  'Прогнозы',
  'Отношения',
  'Финансы',
  'Обучение',
  'Кейсы',
  'Здоровье',
]

export const BLOG_CATEGORIES_I18N: Record<Language, string[]> = {
  ru: [
    'Все',
    'Прогнозы',
    'Отношения',
    'Финансы',
    'Обучение',
    'Кейсы',
    'Здоровье',
  ],
  en: [
    'All',
    'Forecasts',
    'Relationships',
    'Finance',
    'Education',
    'Cases',
    'Health',
  ],
  zh: ['全部', '预测', '关系', '财务', '教育', '案例', '健康'],
  es: [
    'Todos',
    'Pronósticos',
    'Relaciones',
    'Finanzas',
    'Educación',
    'Casos',
    'Salud',
  ],
}

// Category mapping for filtering (internal key → display per language)
export const CATEGORY_KEYS = [
  'all',
  'forecasts',
  'relationships',
  'finance',
  'education',
  'cases',
  'health',
] as const
export type CategoryKey = (typeof CATEGORY_KEYS)[number]

export const CATEGORY_MAP: Record<string, CategoryKey> = {
  Все: 'all',
  Прогнозы: 'forecasts',
  Отношения: 'relationships',
  Финансы: 'finance',
  Обучение: 'education',
  Кейсы: 'cases',
  Здоровье: 'health',
}

export const BLOG_ARTICLES: BlogArticle[] = [
  {
    id: 'aquarius-era',
    slug: 'aquarius-era-what-changes',
    title:
      'Эра Водолея: что изменится в мире и жизни каждого из нас в ближайшие 20 лет',
    excerpt:
      'Плутон окончательно переходит в знак Водолея. Разбираем глобальные тренды, востребованные профессии и способы адаптации к новым энергиям.',
    category: 'Прогнозы',
    date: '2025-03-10',
    readTime: '10 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    featured: true,
    coverColor: 'bg-violet-100',
    content: {
      lead: 'В 2024 году Плутон окончательно перешёл в знак Водолея, где пробудет до 2044 года. Это смена эпох — такая же значимая, как переход Плутона в Козерога в 2008 году, совпавший с мировым финансовым кризисом и революцией смартфонов. Что нас ждёт на этот раз?',
      sections: [
        {
          id: 'what-changes',
          heading: 'Что меняется глобально',
          paragraphs: [
            'Плутон в Водолее — это трансформация всего, что связано с технологиями, свободой, коллективным сознанием и социальными структурами. Водолей — знак будущего, инноваций и равенства.',
            'Мы уже видим первые признаки: взрывной рост ИИ, децентрализация финансов, удалённая работа как норма, движения за равные права по всему миру. Всё это усилится многократно.',
          ],
          table: {
            headers: ['Период', 'Плутон в знаке', 'Ключевые события'],
            rows: [
              [
                '1995–2008',
                'Стрелец',
                'Глобализация, интернет-бум, расширение ЕС',
              ],
              [
                '2008–2024',
                'Козерог',
                'Финансовый кризис, смартфоны, корпоративная власть',
              ],
              [
                '2024–2044',
                'Водолей',
                'ИИ-революция, децентрализация, новые социальные модели',
              ],
            ],
          },
        },
        {
          id: 'career',
          heading: 'Какие профессии станут востребованными',
          paragraphs: [
            'Водолей управляет технологиями, наукой и всем нестандартным. Профессии будущего:',
          ],
          list: [
            'Специалисты по ИИ и машинному обучению',
            'Эксперты по кибербезопасности и цифровой этике',
            'Разработчики в сфере зелёных технологий',
            'Психологи и коучи (рост потребности в человечности)',
            'Креативные профессии, которые ИИ не заменит',
          ],
        },
        {
          id: 'personal',
          heading: 'Как это затронет лично вас',
          paragraphs: [
            'Влияние Плутона зависит от того, в каком доме вашей натальной карты находится Водолей. Если Водолей на куспиде 10 дома — ждите кардинальных карьерных перемен. На 7 доме — трансформация отношений. На 2 доме — полная перестройка финансовой системы.',
            'Самое важное: не сопротивляйтесь переменам. Плутон разрушает то, что устарело, чтобы освободить место для нового. Чем гибче вы будете, тем легче пройдёте этот период.',
          ],
        },
        {
          id: 'how-to-adapt',
          heading: 'Как адаптироваться',
          paragraphs: ['Практические рекомендации на ближайшие годы:'],
          list: [
            'Учитесь новому постоянно — Водолей не терпит стагнации',
            'Развивайте сеть контактов — Водолей про коллектив, не про одиночек',
            'Будьте открыты к нестандартным решениям',
            'Инвестируйте в технологические навыки',
            'Закажите натальную карту, чтобы понять, какой дом активирует Плутон именно у вас',
          ],
        },
      ],
    },
    relatedService: 'service-forecast',
    relatedServiceLabel: 'Узнайте, как Плутон влияет на вашу карту',
    relatedServiceCta: 'Заказать прогноз',
    tags: ['Плутон', 'Водолей', 'глобальные тренды', 'прогноз'],
  },
  {
    id: 'retrograde-mercury-survival',
    slug: 'retrograde-mercury-survival-guide',
    title:
      'Как пережить ретроградный Меркурий без потерь: инструкция по выживанию',
    excerpt:
      'Разбираем основные мифы и даём практические советы, как использовать этот период себе во благо.',
    category: 'Прогнозы',
    date: '2025-03-05',
    readTime: '5 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-indigo-100',
    content: {
      lead: 'Три-четыре раза в год интернет наполняется шутками и страхами о ретроградном Меркурии. Ему приписывают все беды: от сломанного телефона до увольнения. Давайте разберёмся, так ли страшен этот период и как обернуть его себе на пользу.',
      sections: [
        {
          id: 'what-is',
          heading: 'Что такое ретроградность?',
          paragraphs: [
            'С астрономической точки зрения планеты не могут двигаться назад. Ретроградность — это оптическая иллюзия, возникающая из-за разницы в скоростях движения Земли и других планет вокруг Солнца.',
            'В астрологии Меркурий отвечает за коммуникации, информацию, торговлю, документы, поездки и технику. Когда он «пятится назад», эти сферы начинают тормозить, требовать пересмотра и доработки.',
          ],
          quote:
            'Ретроградный Меркурий — это не наказание, а космический тайм-аут. Время остановиться, оглянуться назад и навести порядок в делах.',
        },
        {
          id: 'myths',
          heading: 'Главные мифы',
          paragraphs: [
            'Миф №1: «Нельзя вообще ничего делать». Неправда — жизнь не останавливается. Просто будьте внимательнее к деталям.',
            'Миф №2: «Всё сломается». Техника ломается круглый год. Просто в ретроград мы обращаем на это больше внимания.',
            'Миф №3: «Нельзя начинать отношения». Можно, но будьте готовы к пересмотру ожиданий.',
          ],
        },
        {
          id: 'what-to-do',
          heading: 'Что МОЖНО и НУЖНО делать',
          paragraphs: ['Ретроградный Меркурий идеален для:'],
          list: [
            'Завершения начатого — дописать статью, доделать ремонт, закрыть старые проекты',
            'Наведения порядка — разбирать шкафы, чистить память телефона',
            'Возобновления старых связей — встречи со старыми друзьями',
            'Ремонта и переделки — чинить технику, переписывать тексты',
          ],
        },
        {
          id: 'what-not-to-do',
          heading: 'Чего делать НЕ СТОИТ',
          paragraphs: ['Лучше отложить, если есть возможность:'],
          list: [
            'Покупку сложной техники (смартфоны, компьютеры, автомобили)',
            'Подписание важных долгосрочных контрактов',
            'Запуск принципиально новых проектов',
            'Выход на новую работу (могут измениться условия)',
          ],
        },
        {
          id: 'conclusion',
          heading: 'Резюме',
          paragraphs: [
            'Не стоит бояться ретроградного Меркурия. Используйте это время для паузы и рефлексии. Если вам всё же необходимо подписать договор или купить технику — просто будьте в два раза внимательнее, читайте мелкий шрифт и сохраняйте чеки.',
          ],
        },
      ],
    },
    relatedService: 'service-forecast',
    relatedServiceLabel: 'В каком доме вашей карты идёт транзит Меркурия?',
    relatedServiceCta: 'Заказать прогноз',
    tags: ['Меркурий', 'ретроград', 'транзиты', 'практика'],
  },
  {
    id: 'synastry-wrong-partners',
    slug: 'synastry-why-wrong-partners',
    title: 'Синастрия: почему мы выбираем «не тех» партнёров',
    excerpt:
      'Астрологический взгляд на паттерны в отношениях. Почему нас тянет к определённым людям и как это изменить.',
    category: 'Отношения',
    date: '2025-02-28',
    readTime: '7 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-rose-100',
    content: {
      lead: 'Знакомая ситуация: вы снова и снова выбираете похожих партнёров, и каждый раз отношения заканчиваются одинаково. Астрология может объяснить, почему это происходит — и как разорвать цикл.',
      sections: [
        {
          id: 'venus-mars',
          heading: 'Венера и Марс: что вас привлекает',
          paragraphs: [
            'Венера в вашей карте показывает, что вы считаете красивым и ценным в партнёре. Марс — какой тип энергии вас возбуждает. Эти две планеты формируют ваш «тип».',
            'Например, Венера в Скорпионе тянется к интенсивным, загадочным людям. Марс в Овне привлекает напористость и страсть. Проблема в том, что «привлекает» и «подходит для долгих отношений» — это разные вещи.',
          ],
        },
        {
          id: 'moon-needs',
          heading: 'Луна: ваши реальные потребности',
          paragraphs: [
            'Луна показывает, что вам нужно для эмоционального комфорта. Часто Луна и Венера хотят разного. Венера в Водолее хочет свободу и необычность, а Луна в Раке — уют и стабильность.',
            'Когда вы выбираете партнёра только по Венере (влюблённость), игнорируя Луну (потребности), отношения рано или поздно начинают «душить» или «не насыщать».',
          ],
        },
        {
          id: 'saturn-patterns',
          heading: 'Сатурн: кармические паттерны',
          paragraphs: [
            'Сатурн в синастрии показывает, где вы «застреваете». Если Сатурн партнёра стоит на вашей Венере — вы можете чувствовать, что вас не ценят или ограничивают в любви. Это больно, но именно такие аспекты заставляют расти.',
            'Кармические отношения (с участием лунных узлов) — ещё одна причина, почему мы выбираем «не тех». Южный узел тянет к знакомому, но устаревшему. Северный узел пугает, но ведёт к росту.',
          ],
        },
        {
          id: 'how-to-break',
          heading: 'Как разорвать цикл',
          paragraphs: ['Практические шаги:'],
          list: [
            'Закажите анализ совместимости — поймите свои паттерны',
            'Обратите внимание на Луну партнёра, а не только на химию',
            'Ищите баланс гармоничных и напряжённых аспектов',
            'Не бойтесь отношений, которые кажутся «скучными» — возможно, это именно то, что нужно вашей Луне',
          ],
        },
      ],
    },
    relatedService: 'service-compatibility',
    relatedServiceLabel: 'Разберитесь в паттернах ваших отношений',
    relatedServiceCta: 'Заказать синастрию',
    tags: ['синастрия', 'отношения', 'Венера', 'Луна', 'совместимость'],
  },
  {
    id: 'financial-code-natal',
    slug: 'financial-code-natal-chart',
    title: 'Финансовый код в натальной карте: где ваши деньги?',
    excerpt:
      'Какие дома и планеты отвечают за финансы, и как использовать эти знания для увеличения дохода.',
    category: 'Финансы',
    date: '2025-02-20',
    readTime: '8 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-emerald-100',
    content: {
      lead: 'Деньги — одна из самых частых тем на консультациях у астролога. И это неудивительно: натальная карта содержит подробную «финансовую карту» — нужно только уметь её читать.',
      sections: [
        {
          id: 'second-house',
          heading: '2 дом: ваш заработок',
          paragraphs: [
            'Второй дом натальной карты отвечает за личные деньги, заработок, ценности и ресурсы. Знак на куспиде 2 дома показывает ваш стиль зарабатывания. Планеты во 2 доме — конкретные инструменты.',
            'Например, Юпитер во 2 доме — потенциал больших заработков (но и больших трат). Сатурн — медленный, но стабильный рост дохода. Уран — нестабильный, но потенциально высокий доход из нестандартных источников.',
          ],
        },
        {
          id: 'eighth-house',
          heading: '8 дом: чужие деньги',
          paragraphs: [
            'Восьмой дом — это деньги других людей: наследство, кредиты, инвестиции, страховки, деньги партнёра. Если у вас сильный 8 дом, вы можете хорошо управлять чужими финансами.',
            'Плутон или Юпитер в 8 доме часто указывают на способность привлекать ресурсы извне — через инвестиции, гранты, партнёрства.',
          ],
        },
        {
          id: 'venus-jupiter',
          heading: 'Венера и Юпитер: планеты денег',
          paragraphs: [
            'Венера — малый благодетель, отвечает за повседневные финансы и ценности. Юпитер — большой благодетель, отвечает за крупные суммы и финансовый рост. Их положение в знаках и домах показывает, откуда и как к вам приходят деньги.',
          ],
          list: [
            'Венера в земных знаках — стабильный доход, материальные ценности',
            'Венера в воздушных знаках — доход через коммуникации и связи',
            'Юпитер в 10 доме — карьерный рост приносит деньги',
            'Юпитер в 1 доме — деньги приходят через личную инициативу',
          ],
        },
        {
          id: 'transits-money',
          heading: 'Транзиты и финансовые периоды',
          paragraphs: [
            'Юпитер проходит через ваш 2 дом раз в 12 лет — это лучший период для увеличения дохода. Сатурн через 2 дом — время финансовой дисциплины и построения фундамента. Уран — неожиданные финансовые перемены.',
            'Знание этих циклов позволяет планировать крупные финансовые решения: когда брать кредит, когда инвестировать, когда копить.',
          ],
        },
      ],
    },
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнайте свой финансовый код',
    relatedServiceCta: 'Заказать натальную карту',
    tags: ['финансы', 'деньги', '2 дом', '8 дом', 'Юпитер'],
  },
  {
    id: 'saturn-return-crisis',
    slug: 'saturn-return-30-crisis',
    title: 'Кризис 30 лет: возврат Сатурна и как его пережить',
    excerpt:
      'Почему в 28–30 лет жизнь переворачивается с ног на голову, и что с этим делать с точки зрения астрологии.',
    category: 'Обучение',
    date: '2025-02-15',
    readTime: '6 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-slate-200',
    content: {
      lead: 'Если вам 28–30 лет и кажется, что жизнь рушится — поздравляем, у вас возврат Сатурна. Это один из самых мощных и трансформирующих транзитов, через который проходит каждый человек.',
      sections: [
        {
          id: 'what-is-return',
          heading: 'Что такое возврат Сатурна',
          paragraphs: [
            'Сатурн делает полный оборот вокруг Солнца за ~29,5 лет. Когда он возвращается на то же место, где был в момент вашего рождения, начинается «возврат Сатурна» — период длительностью около 2,5 лет.',
            'Сатурн — планета ответственности, структуры и взросления. Его возврат — это «экзамен на взрослость». Всё, что было построено на шатком фундаменте, начинает рушиться. Всё подлинное — укрепляется.',
          ],
        },
        {
          id: 'what-happens',
          heading: 'Что обычно происходит',
          paragraphs: ['Типичные проявления возврата Сатурна:'],
          list: [
            'Смена карьеры или осознание, что вы «не на своём месте»',
            'Разрыв отношений, которые держались по инерции',
            'Переезд, смена круга общения',
            'Кризис идентичности: «Кто я? Чего я хочу на самом деле?»',
            'Проблемы со здоровьем как сигнал изменить образ жизни',
          ],
        },
        {
          id: 'how-to-survive',
          heading: 'Как пережить',
          paragraphs: [
            'Главное правило: не сопротивляйтесь. Сатурн разрушает только то, что не работает. Если отношения рушатся — значит, они не были настоящими. Если карьера трещит — значит, это не ваш путь.',
            'Используйте этот период для честной ревизии жизни. Задайте себе вопросы: «Это действительно моё? Я делаю это потому что хочу, или потому что так надо?»',
          ],
          quote:
            'Возврат Сатурна — это не наказание. Это приглашение стать тем, кем вы действительно являетесь, а не тем, кем вас хотели видеть другие.',
        },
      ],
    },
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнайте, где Сатурн в вашей карте',
    relatedServiceCta: 'Заказать натальную карту',
    tags: ['Сатурн', 'возврат Сатурна', 'кризис', '30 лет', 'транзиты'],
  },
  {
    id: 'moon-beauty-calendar',
    slug: 'moon-beauty-calendar-guide',
    title: 'Лунный календарь красоты: когда стричь волосы и делать процедуры',
    excerpt:
      'Как фазы Луны влияют на красоту и здоровье, и как использовать лунный цикл для ухода за собой.',
    category: 'Здоровье',
    date: '2025-02-10',
    readTime: '5 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-pink-100',
    content: {
      lead: 'Луна влияет на приливы и отливы, рост растений и наше самочувствие. Многие женщины замечают, что результат косметических процедур зависит от лунного дня. Разбираемся, есть ли в этом смысл.',
      sections: [
        {
          id: 'growing-moon',
          heading: 'Растущая Луна: время для роста',
          paragraphs: [
            'От новолуния до полнолуния Луна растёт — и вместе с ней растёт всё в нашем теле. Это лучшее время для:',
          ],
          list: [
            'Стрижки волос (если хотите, чтобы росли быстрее)',
            'Питательных масок и процедур',
            'Начала новой программы ухода',
            'Маникюра с укреплением ногтей',
          ],
        },
        {
          id: 'full-moon',
          heading: 'Полнолуние: пик энергии',
          paragraphs: [
            'В полнолуние организм на пике чувствительности. Кожа лучше впитывает питательные вещества, но и более склонна к раздражению. Хорошо делать глубокое увлажнение, но лучше избегать агрессивных пилингов и инъекций.',
          ],
        },
        {
          id: 'waning-moon',
          heading: 'Убывающая Луна: время очищения',
          paragraphs: [
            'От полнолуния до новолуния — время для очищения и избавления от лишнего:',
          ],
          list: [
            'Чистки лица, пилинги, детокс-процедуры',
            'Эпиляция (волосы растут медленнее)',
            'Стрижка (если хотите сохранить форму дольше)',
            'Разгрузочные дни и детокс',
          ],
        },
        {
          id: 'moon-signs',
          heading: 'Луна в знаках зодиака',
          paragraphs: [
            'Помимо фазы, важен знак, в котором находится Луна. Луна в Тельце и Весах — лучшие дни для процедур красоты. Луна в Овне и Скорпионе — лучше избегать хирургических вмешательств. Луна в Рыбах — повышенная чувствительность кожи.',
          ],
        },
      ],
    },
    relatedService: 'moon-calendar',
    relatedServiceLabel: 'Смотрите актуальный лунный календарь',
    relatedServiceCta: 'Открыть календарь',
    tags: ['Луна', 'красота', 'здоровье', 'лунный календарь'],
  },
  {
    id: 'how-to-choose-astrologer',
    slug: 'how-to-choose-astrologer-7-signs',
    title: 'Как выбрать астролога: 7 признаков профессионала',
    excerpt:
      'На что обращать внимание при выборе астролога, и какие «красные флаги» должны вас насторожить.',
    category: 'Обучение',
    date: '2025-02-05',
    readTime: '6 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-amber-100',
    content: {
      lead: 'Рынок астрологических услуг растёт, и вместе с ним растёт количество непрофессионалов. Как отличить настоящего астролога от шарлатана? Вот 7 ключевых признаков.',
      sections: [
        {
          id: 'green-flags',
          heading: '7 признаков хорошего астролога',
          paragraphs: ['На что обращать внимание:'],
          list: [
            'Просит точное время, дату и место рождения — без этого невозможен серьёзный анализ',
            'Говорит о тенденциях, а не о конкретных событиях — «период благоприятен для», а не «вы женитесь 15 марта»',
            'Не пугает и не манипулирует — профессионал не скажет «на вас порча, заплатите за снятие»',
            'Имеет образование и опыт — курсы, школа, практика от 2–3 лет',
            'Объясняет, а не диктует — хороший астролог помогает понять себя, а не принимает решения за вас',
            'Имеет отзывы и портфолио — реальные клиенты, кейсы, публикации',
            'Соблюдает этику — не разглашает данные клиентов, не осуждает',
          ],
        },
        {
          id: 'red-flags',
          heading: 'Красные флаги',
          paragraphs: ['Бегите, если астролог:'],
          list: [
            'Обещает «снять порчу» или «привлечь любовь» за деньги',
            'Пугает смертью, болезнями, катастрофами',
            'Не спрашивает время рождения',
            'Даёт прогнозы только по солнечному знаку',
            'Говорит, что ваша судьба предопределена и ничего нельзя изменить',
            'Требует предоплату за «ритуалы» и «обряды»',
          ],
        },
        {
          id: 'where-to-find',
          heading: 'Где искать',
          paragraphs: [
            'Лучший способ — рекомендации знакомых. Также можно искать на специализированных платформах (как AstroGuide), где астрологи проходят проверку квалификации. Обращайте внимание на отзывы, стаж и специализацию.',
          ],
        },
      ],
    },
    relatedService: 'astrologers',
    relatedServiceLabel: 'Проверенные астрологи на AstroGuide',
    relatedServiceCta: 'Выбрать астролога',
    tags: ['выбор астролога', 'консультация', 'советы', 'обучение'],
  },
  {
    id: 'astrology-career',
    slug: 'astrology-career-natal-chart',
    title: 'Астрология и карьера: как найти своё призвание по натальной карте',
    excerpt:
      'MC, 10 дом, Сатурн — какие элементы карты указывают на идеальную профессию и карьерный путь.',
    category: 'Финансы',
    date: '2025-01-28',
    readTime: '7 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-blue-100',
    content: {
      lead: '«Чем мне заниматься?» — один из самых частых вопросов к астрологу. И натальная карта действительно может подсказать направление. Разбираем ключевые индикаторы карьеры.',
      sections: [
        {
          id: 'mc-tenth',
          heading: 'MC и 10 дом: ваше призвание',
          paragraphs: [
            'Середина Неба (MC) — это куспид 10 дома, точка карьеры и публичного образа. Знак на MC показывает, каким вас видит мир и в какой сфере вы можете достичь наибольшего успеха.',
            'MC в Овне — лидерство, предпринимательство, спорт. MC в Тельце — финансы, искусство, недвижимость. MC в Близнецах — медиа, журналистика, преподавание. И так далее для каждого знака.',
          ],
        },
        {
          id: 'saturn-role',
          heading: 'Сатурн: где вы станете мастером',
          paragraphs: [
            'Сатурн показывает сферу, где вы должны упорно трудиться, но где со временем достигнете мастерства. Сатурн в 3 доме — мастерство в коммуникациях. В 6 доме — в медицине или сервисе. В 9 доме — в образовании или юриспруденции.',
          ],
        },
        {
          id: 'sixth-house',
          heading: '6 дом: ежедневная работа',
          paragraphs: [
            'Если 10 дом — это карьера и амбиции, то 6 дом — это повседневная работа и рутина. Знак на куспиде 6 дома показывает, какой тип ежедневной деятельности вам подходит. Планеты в 6 доме — конкретные навыки и инструменты.',
          ],
        },
        {
          id: 'practical-tips',
          heading: 'Практические советы',
          paragraphs: ['Как использовать астрологию для карьеры:'],
          list: [
            'Определите знак на MC — это ваш карьерный архетип',
            'Найдите управителя 10 дома — его положение покажет, через что реализуется карьера',
            'Посмотрите транзиты к MC — когда ждать карьерных перемен',
            'Обратите внимание на Юпитер — он показывает, где вам везёт',
          ],
        },
      ],
    },
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнайте свой карьерный код',
    relatedServiceCta: 'Заказать натальную карту',
    tags: ['карьера', 'MC', '10 дом', 'Сатурн', 'призвание'],
  },
  {
    id: 'full-moon-guide',
    slug: 'full-moon-complete-guide',
    title: 'Полнолуние: полная инструкция по применению',
    excerpt:
      'Что делать в полнолуние, чего избегать и как использовать эту энергию для манифестации и отпускания.',
    category: 'Прогнозы',
    date: '2025-01-20',
    readTime: '5 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-yellow-100',
    content: {
      lead: 'Полнолуние — самая мощная фаза лунного цикла. Эмоции на пике, энергия зашкаливает, и всё, что было скрыто, выходит на поверхность. Как использовать эту силу?',
      sections: [
        {
          id: 'what-happens',
          heading: 'Что происходит в полнолуние',
          paragraphs: [
            'В полнолуние Солнце и Луна находятся в оппозиции — напротив друг друга. Это создаёт напряжение между рациональным (Солнце) и эмоциональным (Луна). Всё, что копилось с новолуния, достигает кульминации.',
            'Полнолуние — это время результатов, завершений и осознаний. То, что вы начали в новолуние, сейчас приносит плоды (или показывает, что не работает).',
          ],
        },
        {
          id: 'do-list',
          heading: 'Что делать в полнолуние',
          paragraphs: ['Лучшие практики:'],
          list: [
            'Отпускайте — напишите список того, от чего хотите избавиться, и сожгите его',
            'Благодарите — полнолуние усиливает благодарность',
            'Медитируйте — энергия полнолуния идеальна для глубокой медитации',
            'Завершайте дела — доводите проекты до конца',
            'Проводите ритуалы очищения — ванна с солью, уборка дома',
          ],
        },
        {
          id: 'avoid-list',
          heading: 'Чего избегать',
          paragraphs: ['В полнолуние лучше не:'],
          list: [
            'Принимать импульсивные решения — эмоции искажают восприятие',
            'Начинать новые проекты — это время завершения, не начала',
            'Конфликтовать — напряжение и так высокое',
            'Делать операции (если не срочно) — повышенная кровоточивость',
          ],
        },
      ],
    },
    relatedService: 'moon-calendar',
    relatedServiceLabel: 'Следите за фазами Луны',
    relatedServiceCta: 'Открыть лунный календарь',
    tags: ['полнолуние', 'Луна', 'ритуалы', 'практика'],
  },
  {
    id: 'child-natal-chart',
    slug: 'child-natal-chart-parenting',
    title:
      'Детская натальная карта: как понять своего ребёнка через астрологию',
    excerpt:
      'Зачем строить карту ребёнку, что она покажет и как использовать эти знания в воспитании.',
    category: 'Кейсы',
    date: '2025-01-15',
    readTime: '6 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-cyan-100',
    content: {
      lead: 'Каждый ребёнок уникален, и натальная карта может помочь родителям понять эту уникальность. Не для того, чтобы «запрограммировать» ребёнка, а чтобы поддержать его природу.',
      sections: [
        {
          id: 'why-build',
          heading: 'Зачем строить карту ребёнку',
          paragraphs: [
            'Натальная карта ребёнка показывает его врождённый темперамент, стиль обучения, эмоциональные потребности и потенциальные таланты. Это не приговор и не предсказание — это карта возможностей.',
            'Зная карту ребёнка, вы можете адаптировать стиль воспитания. Ребёнок с Луной в Водолее нуждается в свободе и уважении к его необычности. Ребёнок с Луной в Раке — в тепле, объятиях и стабильности.',
          ],
        },
        {
          id: 'what-to-look',
          heading: 'На что обращать внимание',
          paragraphs: ['Ключевые элементы детской карты:'],
          list: [
            'Луна — эмоциональные потребности ребёнка (самое важное!)',
            'Меркурий — стиль обучения и коммуникации',
            'Марс — как ребёнок проявляет энергию и агрессию',
            '5 дом — творческие способности и хобби',
            'Асцендент — как ребёнок ведёт себя в новых ситуациях',
          ],
        },
        {
          id: 'examples',
          heading: 'Примеры из практики',
          paragraphs: [
            'Ребёнок с Меркурием в Близнецах учится быстро, но поверхностно — ему нужно разнообразие, а не зубрёжка. Ребёнок с Меркурием в Тельце учится медленно, но основательно — не торопите его.',
            'Марс в Овне — активный, спортивный ребёнок, которому нужно много двигаться. Марс в Рыбах — мечтательный, ему подойдут плавание, танцы, йога.',
          ],
        },
      ],
    },
    relatedService: 'service-natal',
    relatedServiceLabel: 'Постройте карту вашего ребёнка',
    relatedServiceCta: 'Заказать натальную карту',
    tags: ['дети', 'воспитание', 'натальная карта', 'Луна', 'Меркурий'],
  },
  {
    id: 'jupiter-gemini-2025',
    slug: 'jupiter-gemini-2024-2025',
    title: 'Юпитер в Близнецах 2024–2025: кому повезёт больше всех',
    excerpt:
      'Юпитер вошёл в Близнецы — разбираем, какие знаки получат максимум возможностей и как использовать этот транзит.',
    category: 'Прогнозы',
    date: '2025-01-10',
    readTime: '6 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-orange-100',
    content: {
      lead: 'Юпитер — планета удачи и расширения — перешёл в знак Близнецов, где пробудет до июня 2025 года. Это время возможностей в сфере обучения, коммуникаций и новых связей.',
      sections: [
        {
          id: 'what-means',
          heading: 'Что значит Юпитер в Близнецах',
          paragraphs: [
            'Близнецы — знак информации, общения и многозадачности. Юпитер здесь расширяет всё, что связано с обучением, медиа, путешествиями и социальными связями.',
            'Это отличное время для: запуска блога или подкаста, изучения нового языка, прохождения курсов, расширения круга знакомств, написания книги.',
          ],
        },
        {
          id: 'lucky-signs',
          heading: 'Кому повезёт больше всех',
          paragraphs: ['Максимум возможностей получат:'],
          list: [
            'Близнецы — Юпитер в вашем знаке! Год больших возможностей',
            'Весы и Водолей — гармоничный тригон, лёгкие возможности',
            'Стрелец — оппозиция, рост через партнёрства',
            'Дева и Рыбы — квадратура, рост через вызовы',
          ],
        },
        {
          id: 'how-to-use',
          heading: 'Как использовать',
          paragraphs: ['Практические рекомендации:'],
          list: [
            'Учитесь чему-то новому — Юпитер в Близнецах вознаграждает любопытство',
            'Расширяйте сеть контактов — знакомства сейчас могут изменить жизнь',
            'Пишите, говорите, публикуйтесь — Близнецы про коммуникацию',
            'Путешествуйте (особенно короткие поездки)',
            'Не распыляйтесь — Близнецы могут дать слишком много вариантов',
          ],
        },
      ],
    },
    relatedService: 'service-forecast',
    relatedServiceLabel: 'Как Юпитер влияет на вашу карту',
    relatedServiceCta: 'Заказать прогноз',
    tags: ['Юпитер', 'Близнецы', 'прогноз 2025', 'удача'],
  },
  {
    id: 'case-career-change',
    slug: 'case-career-change-35',
    title: 'Кейс: как натальная карта помогла сменить профессию в 35 лет',
    excerpt:
      'Реальная история клиентки, которая ушла из бухгалтерии в дизайн интерьеров — и астрология подтвердила, что это правильный путь.',
    category: 'Кейсы',
    date: '2025-01-05',
    readTime: '5 мин',
    author: 'Редакция AstroGuide',
    authorInitials: 'АГ',
    coverColor: 'bg-teal-100',
    content: {
      lead: 'Марина, 35 лет, бухгалтер с 10-летним стажем. Пришла на консультацию с вопросом: «Я ненавижу свою работу, но боюсь менять. Что говорит карта?» Вот что мы обнаружили.',
      sections: [
        {
          id: 'initial-situation',
          heading: 'Исходная ситуация',
          paragraphs: [
            'Марина работала бухгалтером с 25 лет. Стабильная зарплата, понятный карьерный путь. Но последние 3 года она чувствовала нарастающее выгорание и мечтала о чём-то творческом.',
            'Её подруга подарила ей натальную карту на день рождения. Результат оказался неожиданным.',
          ],
        },
        {
          id: 'what-chart-showed',
          heading: 'Что показала карта',
          paragraphs: [
            'MC (карьера) в Весах — знак эстетики, дизайна, гармонии. Управитель MC — Венера — в 5 доме (творчество) в Тельце. Это классическая «карта дизайнера».',
            'Сатурн во 2 доме объяснял, почему она выбрала бухгалтерию — страх финансовой нестабильности. Но Юпитер в 10 доме говорил о потенциале большого карьерного успеха — именно в творческой сфере.',
          ],
        },
        {
          id: 'transit-trigger',
          heading: 'Транзитный триггер',
          paragraphs: [
            'В момент консультации транзитный Уран (планета перемен) проходил по её Солнцу. Это классический транзит «пробуждения» — когда человек больше не может жить по-старому. Плюс транзитный Юпитер входил в её 10 дом — окно возможностей для карьерных перемен.',
          ],
        },
        {
          id: 'result',
          heading: 'Результат',
          paragraphs: [
            'Марина прошла курсы дизайна интерьеров, параллельно работая бухгалтером. Через 8 месяцев получила первый заказ. Через полтора года полностью перешла в дизайн. Сейчас зарабатывает больше, чем в бухгалтерии, и впервые за годы чувствует, что на своём месте.',
          ],
          quote:
            'Карта не сказала мне «уходи из бухгалтерии». Она показала, что у меня есть потенциал для творческой карьеры. Решение я приняла сама — но с пониманием, что звёзды на моей стороне.',
        },
      ],
    },
    relatedService: 'service-natal',
    relatedServiceLabel: 'Узнайте свой карьерный потенциал',
    relatedServiceCta: 'Заказать натальную карту',
    tags: ['кейс', 'карьера', 'смена профессии', 'натальная карта'],
  },
]

// Multilingual date formatting
const MONTHS: Record<Language, string[]> = {
  ru: [
    'Января',
    'Февраля',
    'Марта',
    'Апреля',
    'Мая',
    'Июня',
    'Июля',
    'Августа',
    'Сентября',
    'Октября',
    'Ноября',
    'Декабря',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  zh: [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ],
  es: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
}

export function formatBlogDate(dateStr: string, lang: Language = 'ru'): string {
  const d = new Date(dateStr)
  const months = MONTHS[lang] || MONTHS.ru
  if (lang === 'zh') {
    return `${d.getFullYear()}年${months[d.getMonth()]}${d.getDate()}日`
  }
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

// Helper: get article by slug or id
export function findBlogArticle(idOrSlug: string): BlogArticle | undefined {
  return BLOG_ARTICLES.find((a) => a.id === idOrSlug || a.slug === idOrSlug)
}

// Helper: get articles by category
export function getArticlesByCategory(category: string): BlogArticle[] {
  if (category === 'Все' || category === 'all') return BLOG_ARTICLES
  return BLOG_ARTICLES.filter((a) => a.category === category)
}

// Helper: generate SEO from article data (fallback if seo field not set)
export function getArticleSeo(
  article: BlogArticle,
  lang: Language = 'ru',
): BlogSeo {
  if (article.seo?.[lang]) return article.seo[lang]!
  // Fallback: generate from article fields
  const siteName = 'AstroGuide'
  return {
    title: `${article.title} | ${siteName}`,
    description: article.excerpt,
    keywords: article.tags.join(', '),
  }
}

// Blog UI translations
export const BLOG_UI: Record<Language, Record<string, string>> = {
  ru: {
    title: 'Блог',
    subtitle: 'Полезные статьи об астрологии, психологии и самопознании',
    search: 'Поиск статей...',
    featured: 'Главное',
    found: 'Найдено статей',
    notFound: 'Ничего не найдено',
    notFoundHint:
      'Попробуйте изменить поисковый запрос или выбрать другую категорию',
    resetFilters: 'Сбросить фильтры',
    readTime: 'чтения',
    backToBlog: 'Назад в блог',
    share: 'Поделиться',
    toc: 'Содержание',
    tags: 'Теги',
    readAlso: 'Читайте также',
    allArticles: 'Все статьи',
    articleNotFound: 'Статья не найдена',
    articleNotFoundHint:
      'Возможно, она была удалена или вы перешли по неверной ссылке.',
    backToBlogBtn: 'Вернуться в блог',
    viewCards: 'Карточки',
    viewTable: 'Таблица',
    tableTitle: 'Заголовок',
    tableCategory: 'Рубрика',
    tableDate: 'Дата',
    tableReadTime: 'Время',
    linkCopied: 'Ссылка скопирована в буфер обмена',
  },
  en: {
    title: 'Blog',
    subtitle: 'Useful articles about astrology, psychology and self-discovery',
    search: 'Search articles...',
    featured: 'Featured',
    found: 'Articles found',
    notFound: 'Nothing found',
    notFoundHint:
      'Try changing your search query or selecting a different category',
    resetFilters: 'Reset filters',
    readTime: 'read',
    backToBlog: 'Back to blog',
    share: 'Share',
    toc: 'Contents',
    tags: 'Tags',
    readAlso: 'Read also',
    allArticles: 'All articles',
    articleNotFound: 'Article not found',
    articleNotFoundHint:
      'It may have been deleted or you followed an incorrect link.',
    backToBlogBtn: 'Back to blog',
    viewCards: 'Cards',
    viewTable: 'Table',
    tableTitle: 'Title',
    tableCategory: 'Category',
    tableDate: 'Date',
    tableReadTime: 'Time',
    linkCopied: 'Link copied to clipboard',
  },
  zh: {
    title: '博客',
    subtitle: '关于占星术、心理学和自我认知的有用文章',
    search: '搜索文章...',
    featured: '精选',
    found: '找到文章',
    notFound: '未找到',
    notFoundHint: '尝试更改搜索查询或选择其他类别',
    resetFilters: '重置筛选',
    readTime: '阅读',
    backToBlog: '返回博客',
    share: '分享',
    toc: '目录',
    tags: '标签',
    readAlso: '推荐阅读',
    allArticles: '所有文章',
    articleNotFound: '文章未找到',
    articleNotFoundHint: '它可能已被删除或您访问了错误的链接。',
    backToBlogBtn: '返回博客',
    viewCards: '卡片',
    viewTable: '表格',
    tableTitle: '标题',
    tableCategory: '分类',
    tableDate: '日期',
    tableReadTime: '时间',
    linkCopied: '链接已复制到剪贴板',
  },
  es: {
    title: 'Blog',
    subtitle:
      'Artículos útiles sobre astrología, psicología y autoconocimiento',
    search: 'Buscar artículos...',
    featured: 'Destacado',
    found: 'Artículos encontrados',
    notFound: 'Nada encontrado',
    notFoundHint: 'Intenta cambiar tu búsqueda o seleccionar otra categoría',
    resetFilters: 'Restablecer filtros',
    readTime: 'lectura',
    backToBlog: 'Volver al blog',
    share: 'Compartir',
    toc: 'Contenido',
    tags: 'Etiquetas',
    readAlso: 'Lee también',
    allArticles: 'Todos los artículos',
    articleNotFound: 'Artículo no encontrado',
    articleNotFoundHint:
      'Puede haber sido eliminado o seguiste un enlace incorrecto.',
    backToBlogBtn: 'Volver al blog',
    viewCards: 'Tarjetas',
    viewTable: 'Tabla',
    tableTitle: 'Título',
    tableCategory: 'Categoría',
    tableDate: 'Fecha',
    tableReadTime: 'Tiempo',
    linkCopied: 'Enlace copiado al portapapeles',
  },
}
